import { z } from 'zod';

/**
 * Реестр событий и интерфейс шины.
 *
 * Правило, которое здесь закреплено: **имена внешних систем за пределы своего
 * сервиса не выходят.** Вебхук ЮKassa `payment.succeeded` превращается в наше
 * `patient_payment.succeeded` внутри billing, поэтому смена платёжного
 * провайдера не затрагивает Finance и Notifications.
 *
 * Второе правило: событие несёт идентификаторы, а не медицинские данные.
 * Диагнозы и коды МКБ в шину не попадают.
 *
 * Имена совпадают с таблицами «События» в docs/03-modules/. Событие, которого
 * нет в этом реестре, не публикуется: имя, придуманное в обработчике, потом
 * невозможно найти.
 */

export const EVENTS = {
  identity: [
    'tenant.created',
    'membership.created',
    'membership.role_changed',
    'membership.deactivated',
    'session.suspicious_reuse_detected',
    'device.revoked',
    'support_grant.issued',
    /**
     * Владелец закрыл доступ раньше срока. Отдельно от `expired`: истечение —
     * это часы, а отзыв — решение человека, и подписчику разница видна.
     */
    'support_grant.revoked',
    'support_grant.expired',
    'auth.password_reset_requested',
    'tenant.deletion_requested',
  ],
  patients: [
    'patient.created',
    'patient.updated',
    'patient.merged',
    'patient.deactivated',
    'patient.consent_granted',
    'patient.consent_withdrawn',
    'patient.medical_data_accessed',
  ],
  clinical: [
    'tooth.diagnosis_recorded',
    'tooth.procedure_performed',
    'tooth.restoration_installed',
    'tooth.extracted',
    'tooth.event_corrected',
    'periodontal.measured',
    'warranty.expiring_soon',
  ],
  treatmentPlans: [
    'treatment_plan.created',
    'treatment_plan.proposed',
    /** С `optionId` и `tier`: по ним считают, какой вариант выбирают чаще. */
    'treatment_plan.agreed',
    /** С причиной отказа — главной коммерческой метрикой модуля. */
    'treatment_plan.cancelled',
    /** Составлена новая версия взамен согласованной. */
    'treatment_plan.versioned',
    'treatment_plan.completed',
  ],
  scheduling: [
    'appointment.created',
    'appointment.confirmed',
    'appointment.arrived',
    'appointment.rescheduled',
    'appointment.cancelled',
    'appointment.completed',
    'appointment.no_show',
    /** Время освободилось: лист ожидания и CRM могут предложить его пациенту. */
    'slot.freed',
  ],
  lab: [
    'lab_order.created',
    'lab_order.accepted',
    'lab_order.status_changed',
    'lab_order.ready_for_try_in',
    'lab_order.ready',
    'lab_order.remake_requested',
    'lab_order.installed',
    'lab_order.closed',
    'lab_order.overdue',
  ],
  finance: [
    'invoice.issued',
    'invoice.paid',
    'invoice.overdue',
    'invoice.cancelled',
    'payment.received',
    'payment.reversed',
    'installment.created',
    'installment.payment_due',
    'installment.defaulted',
    'cash_shift.closed',
    'accrual.created',
    'lab_settlement.closed',
    'service.price_changed',
    /** Прочие расходы (аренда, коммуналка) — веха 3.9, ручной ввод владельца. */
    'manual_expense.created',
    'manual_expense.deactivated',
    /** Отметка «зарплата выплачена» с суммой периода — веха 3.9, отток ДДС. */
    'salary_period.closed',
    /**
     * Кошелёк пациента: аванс внесён (приток ДДС своим способом), зачтён в
     * оплату счёта (не приток — деньги уже были), возвращён (отток).
     */
    'wallet.deposited',
    'wallet.charged',
    'wallet.refunded',
    /**
     * Расход клиники внесён или отменён — отток ДДС по виду расхода и
     * слагаемое управленческой прибыли. Перенесённые из «Прочих расходов»
     * событий не шлют: аналитика знает их как `manual_expense.*`.
     */
    'expense.recorded',
    'expense.cancelled',
    /**
     * Сейф: выдача из кассы — внутреннее перемещение, не отток; изъятие из
     * сейфа — отток.
     */
    'safe.deposited',
    'safe.withdrawn',
  ],
  crm: [
    'crm.task_created',
    'crm.task_completed',
    /** Приёмка поручения/CRM-задачи — расхождение с исходной спекой модуля (было 6 из 7 событий, этого не было). */
    'crm.task_accepted',
    'crm.task_overdue',
    'crm.message_scheduled',
    'crm.feedback_received',
    'crm.campaign_run_completed',
    /**
     * Девять предустановленных кампаний публикуют каждая своё имя, а не
     * общее `crm.message_scheduled`: у `EVENT_NOTIFICATION_SPECS`
     * (`notifications`) `kind`/`priority` — статичные поля на имя события,
     * а «день рождения» и «напоминание о долге» различаются и тем, и
     * другим (marketing/transactional). `crm.message_scheduled` остаётся в
     * реестре для более простых будущих кампаний, где это различие не нужно.
     */
    'crm.birthday_reached',
    'crm.appointment_reminder_due',
    'crm.appointment_confirmation_requested',
    'crm.prophylaxis_due',
    'crm.patient_return_due',
    'crm.feedback_link_requested',
    'crm.warranty_expiring_reminder',
    'crm.debt_reminder_due',
  ],
  inventory: [
    'stock.movement_created',
    'stock.low_balance',
    'stock.batch_expiring',
    'stock.discrepancy_found',
    'stock.shortage_for_appointment',
    'purchase_request.submitted',
    'purchase_request.approved',
    'purchase_request.rejected',
    'inventory_count.approved',
  ],
  instruments: [
    'kit.issued',
    'kit.returned',
    'kit.sterility_expired',
    /** Обеспеченность приёма комплектами (§6–7 спеки) — тот же приём, что у `stock.shortage_for_appointment` склада, в своём модуле. */
    'kit.shortage_for_appointment',
    'sterilizer_load.closed',
    'sterilizer.control_failed',
    'sterilizer.validation_overdue',
    'instrument.resource_exhausted',
    'instrument.written_off',
    'equipment.repair_requested',
    'equipment.status_changed',
    'equipment.maintenance_due',
    'equipment.moved',
    'equipment.decommissioned',
    'equipment.deleted',
  ],
  procurement: [
    'purchase_order.submitted',
    'purchase_order.approved',
    'purchase_order.rejected',
    'purchase_order.sent',
    'purchase_order.status_changed',
    'purchase_order.overdue',
    'purchase_order.closed',
    'goods_receipt.registered',
    'goods_receipt.discrepancy_found',
    'procurement.reorder_recommended',
  ],
  staff: [
    'absence.approved',
    'absence.cancelled',
    'kpi.target_missed',
    'payroll_adjustment.created',
    'staff_period.closed',
  ],
  billing: [
    'subscription.created',
    'subscription.status_changed',
    'subscription.renewed',
    'subscription.payment_failed',
    'subscription.trial_ending',
    'subscription.cancelled',
    /** Владелец отвязал сохранённую карту сам; автосписаний по ней больше не будет. */
    'subscription.payment_method_detached',
    'payment_method.expiring',
    'patient_payment.succeeded',
    'patient_payment.canceled',
  ],
  files: ['file.uploaded', 'file.accessed', 'file.deactivated'],
  chat: ['chat.message_sent', 'chat.mention_created'],
  /**
   * Публикует сервис `notifications` — единственный обратный канал к
   * core-api (журнал доставки читает свою же базу, эти события ни на что не
   * подписаны сейчас; заведены для будущего потребителя аналитики).
   */
  notifications: [
    'notification.sent',
    'notification.delivered',
    'notification.failed',
    'notification.suppressed',
  ],
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS][number];

export const EventEnvelope = z.object({
  eventId: z.uuid(),
  name: z.string().min(1),
  tenantId: z.uuid(),
  /** Время записи в outbox, а не отправки: доставка бывает позже. */
  occurredAt: z.iso.datetime(),
  /** Сквозной идентификатор для трассировки цепочки. */
  correlationId: z.string().min(1),
  payload: z.record(z.string(), z.unknown()),
});
export type EventEnvelope = z.infer<typeof EventEnvelope>;

/**
 * Интерфейс шины. Реализация может быть Kafka или Yandex Message Queue —
 * код публикации и потребления от этого не меняется, что и было условием
 * разрешённого компромисса на первой фазе.
 *
 * Доставка `at-least-once`: обработчик обязан быть идемпотентным. Публикация
 * идёт через transactional outbox, то есть в той же транзакции, что и запись
 * данных, иначе событие уйдёт по откаченной транзакции.
 */
export interface EventBus {
  publish(event: EventEnvelope): Promise<void>;
  subscribe(names: readonly string[], handler: (event: EventEnvelope) => Promise<void>): Promise<void>;
}
